// Business hours configuration and helpers (Ecuador timezone GMT-5)
//
// Source of truth: the backoffice config (db.config.business_config.store_hours),
// served by GET /api/config. The values below are the DEFAULTS used during the
// initial render / prerender and whenever the API is unreachable. They MUST stay
// in sync with DEFAULT_STORE_HOURS in the backend (server.py).
//
// Shape: indexed by JS day-of-week (0=Sunday … 6=Saturday). A day is either
// { open: 'HH:MM', close: 'HH:MM' } or null (closed).
export const businessHours = {
  matriz: {
    0: null,
    1: { open: '10:00', close: '19:00' },
    2: { open: '10:00', close: '19:00' },
    3: { open: '10:00', close: '19:00' },
    4: { open: '10:00', close: '19:00' },
    5: { open: '10:00', close: '19:00' },
    6: { open: '09:00', close: '18:00' }
  },
  sucursal: {
    0: { open: '09:00', close: '13:00' },
    1: null, // Lunes cerrado (desde 2026-07-27)
    2: { open: '10:00', close: '19:00' },
    3: { open: '10:00', close: '19:00' },
    4: { open: '10:00', close: '19:00' },
    5: { open: '10:00', close: '19:00' },
    6: { open: '09:00', close: '18:00' }
  },
  // Centro de producción (Bellavista): no atiende público, no sale en
  // /ubicaciones ni como punto de retiro. Solo despacha a domicilio de lunes a
  // sábado, abriendo antes que las tiendas (08:00).
  produccion: {
    0: null, // Domingo cerrado — ese día los domicilios salen de la Sucursal
    1: { open: '08:00', close: '19:00' },
    2: { open: '08:00', close: '19:00' },
    3: { open: '08:00', close: '19:00' },
    4: { open: '08:00', close: '19:00' },
    5: { open: '08:00', close: '19:00' },
    6: { open: '08:00', close: '18:00' }
  }
};

// Locales que existen en la config. 'produccion' es interno (delivery), los
// otros dos son locales visitables.
export const LOCATION_KEYS = ['matriz', 'sucursal', 'produccion'];

// Locales que pueden DESPACHAR un pedido a domicilio. De lunes a sábado sale
// del centro de producción; los domingos éste no produce y los domicilios
// salen de la Sucursal (Eloy Alfaro, 09:00–13:00). La ventana de entrega de un
// día es la unión de los que estén abiertos, así que basta con cambiar el
// horario de un local en el backoffice para mover las entregas de ese día.
//
// Ojo: el origen del cálculo de TARIFA sigue siendo la Matriz (backend
// MATRIZ_LOCATION). Esto define CUÁNDO se puede pedir, no DESDE DÓNDE se cobra.
export const DELIVERY_DISPATCH_LOCATIONS = ['produccion', 'sucursal'];

// API day-name (monday…sunday) → JS day-of-week index (0=Sunday … 6=Saturday).
const DAY_NAME_TO_INDEX = {
  sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6
};

// Hydrate `businessHours` from the API payload (config.store_hours), converting
// { matriz: { monday: {open,close}|null, … }, sucursal: {…}, produccion: {…} }
// into the indexed shape y mutando `businessHours` in place so existing imports
// keep their reference. Missing/invalid locations keep their defaults.
export const applyStoreHoursConfig = (storeHours) => {
  if (!storeHours || typeof storeHours !== 'object') return;
  for (const location of LOCATION_KEYS) {
    const schedule = storeHours[location];
    if (!schedule || typeof schedule !== 'object') continue;
    const indexed = {};
    for (const [dayName, idx] of Object.entries(DAY_NAME_TO_INDEX)) {
      const day = schedule[dayName];
      indexed[idx] = (day && day.open && day.close)
        ? { open: day.open, close: day.close }
        : null;
    }
    businessHours[location] = indexed;
  }
};

// --- Texto legible del horario (español) ---
const DAY_SHORT_ES = { 1: 'Lun', 2: 'Mar', 3: 'Mié', 4: 'Jue', 5: 'Vie', 6: 'Sáb', 0: 'Dom' };
const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0]; // Lunes → Domingo

// Resume una tabla { 0..6: {open,close}|null } en una cadena tipo
// "Lun–Vie 10:00–19:00 · Sáb 09:00–18:00 · Dom cerrado", agrupando días
// consecutivos con el mismo horario.
const summarizeTableEs = (table) => {
  if (!table) return '';
  const groups = [];
  for (const idx of WEEK_ORDER) {
    const d = table[idx];
    const key = d && d.open && d.close ? `${d.open}–${d.close}` : 'cerrado';
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.days.push(idx);
    else groups.push({ key, days: [idx] });
  }
  return groups
    .map((g) => {
      const label = g.days.length === 1
        ? DAY_SHORT_ES[g.days[0]]
        : `${DAY_SHORT_ES[g.days[0]]}–${DAY_SHORT_ES[g.days[g.days.length - 1]]}`;
      return g.key === 'cerrado' ? `${label} cerrado` : `${label} ${g.key}`;
    })
    .join(' · ');
};

// Resumen del horario de UN local ('matriz' | 'sucursal' | 'produccion'). Lee el
// objeto vivo `businessHours`, así que refleja la config del backoffice tras la
// hidratación. Para el horario de domicilios usar getDeliveryScheduleSummaryEs(),
// que no es el de un local sino la unión de los que despachan.
export const getScheduleSummaryEs = (location) => summarizeTableEs(businessHours[location]);

export const getEcuadorTime = () => {
  const now = new Date();
  const ecuadorOffset = -5 * 60;
  const localOffset = now.getTimezoneOffset();
  const diff = ecuadorOffset - (-localOffset);
  return new Date(now.getTime() + diff * 60 * 1000);
};

export const timeToMinutes = (timeStr) => {
  const [hours, mins] = timeStr.split(':').map(Number);
  return hours * 60 + mins;
};

export const minutesToTime = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

// --- Ventana de entrega a domicilio ---
// El delivery no se rige por UN local: es la unión de los que despachan ese día
// (ver DELIVERY_DISPATCH_LOCATIONS). De lunes a sábado despacha el centro de
// producción (08:00); los domingos, que no produce, despacha la Sucursal
// (09:00–13:00). Cuando despacha más de uno, la ventana va del que abre más
// temprano al que cierra más tarde: en la práctica los rangos se solapan (la
// Sucursal está abierta dentro del horario de producción), así que la unión es
// un intervalo continuo y no hay huecos que un min/max escondería.

// Horario de domicilios de un día de la semana (0=Dom … 6=Sáb): { open, close }
// o null si ese día no despacha nadie.
export const getDeliveryHoursForDay = (dayOfWeek) => {
  let open = null;
  let close = null;
  for (const location of DELIVERY_DISPATCH_LOCATIONS) {
    const day = businessHours[location] && businessHours[location][dayOfWeek];
    if (!day || !day.open || !day.close) continue;
    if (open === null || timeToMinutes(day.open) < timeToMinutes(open)) open = day.open;
    if (close === null || timeToMinutes(day.close) > timeToMinutes(close)) close = day.close;
  }
  return open && close ? { open, close } : null;
};

// Igual, pero para una fecha 'YYYY-MM-DD' (mismo contrato que
// getBusinessHoursForDate, que se usa para el retiro en tienda).
export const getDeliveryHoursForDate = (dateStr) =>
  getDeliveryHoursForDay(new Date(dateStr + 'T12:00:00').getDay());

// Horario de domicilios de HOY (hora de Ecuador), o null si hoy no se entrega.
export const getTodayDeliveryHours = () =>
  getDeliveryHoursForDay(getEcuadorDayAndMinutes().dayOfWeek);

// Resumen semanal de domicilios, tipo
// "Lun–Vie 08:00–19:00 · Sáb 08:00–18:00 · Dom 09:00–13:00".
export const getDeliveryScheduleSummaryEs = () =>
  summarizeTableEs(
    WEEK_ORDER.reduce((table, idx) => ({ ...table, [idx]: getDeliveryHoursForDay(idx) }), {})
  );

// Horario que rige un pedido para una fecha dada, según cómo se entrega:
// retiro ('tienda') = el local elegido; domicilio ('delivery') = la ventana de
// despacho de ese día. Es el único lugar donde se decide esto: lo usan la
// validación del carrito, el texto "Horario de entregas" y el valor por defecto
// de la fecha, que antes resolvían cada uno por su cuenta.
export const getHoursForOrder = (deliveryMethod, location, dateStr) =>
  deliveryMethod === 'tienda'
    ? getBusinessHoursForDate(dateStr, location)
    : getDeliveryHoursForDate(dateStr);

// ¿Se está entregando a domicilio AHORA? (cualquier local que despache, abierto)
export const isDeliveryOpen = () => {
  const { currentTime } = getEcuadorDayAndMinutes();
  const hours = getTodayDeliveryHours();
  if (!hours) return false;
  return currentTime >= timeToMinutes(hours.open) && currentTime <= timeToMinutes(hours.close);
};

// Primera fecha/hora que se puede pedir, para precargar "¿cuándo lo necesitas?".
// Depende de CÓMO se entrega: retiro ('tienda') se rige por el local elegido, y
// domicilio ('delivery') por la ventana de despacho del día — más amplia, y los
// domingos existe aunque la Matriz esté cerrada. Antes miraba siempre la Matriz,
// así que un domingo proponía el lunes incluso pidiendo a domicilio.
// Sin argumentos asume retiro en la Matriz, que es el estado inicial del carrito.
export const getNextOpenDateTime = ({ deliveryMethod = 'tienda', location = 'matriz' } = {}) => {
  const hoursForDay = (dayOfWeek) =>
    deliveryMethod === 'tienda'
      ? (businessHours[location] || businessHours.matriz)[dayOfWeek]
      : getDeliveryHoursForDay(dayOfWeek);

  const now = getEcuadorTime();
  const currentDayOfWeek = now.getDay();
  const currentTimeMinutes = now.getHours() * 60 + now.getMinutes();

  const todayHours = hoursForDay(currentDayOfWeek);

  if (todayHours) {
    const openMinutes = timeToMinutes(todayHours.open);
    const closeMinutes = timeToMinutes(todayHours.close);
    
    let earliestMinutes = currentTimeMinutes + 120;
    earliestMinutes = Math.ceil(earliestMinutes / 30) * 30;
    
    if (currentTimeMinutes < openMinutes) {
      earliestMinutes = Math.max(earliestMinutes, openMinutes + 120);
    }
    
    if (earliestMinutes >= openMinutes && earliestMinutes < closeMinutes) {
      const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      return { date: todayStr, time: minutesToTime(earliestMinutes) };
    }
  }
  
  for (let i = 1; i <= 7; i++) {
    const checkDate = new Date(now);
    checkDate.setDate(now.getDate() + i);
    const dayOfWeek = checkDate.getDay();
    const hours = hoursForDay(dayOfWeek);

    if (hours) {
      const openMinutes = timeToMinutes(hours.open);
      const deliveryMinutes = openMinutes + 120;
      const dateStr = `${checkDate.getFullYear()}-${String(checkDate.getMonth() + 1).padStart(2, '0')}-${String(checkDate.getDate()).padStart(2, '0')}`;
      return { date: dateStr, time: minutesToTime(deliveryMinutes) };
    }
  }
  
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  const tomorrowStr = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}`;
  return { date: tomorrowStr, time: '10:30' };
};

export const getBusinessHoursForDate = (dateStr, location) => {
  const date = new Date(dateStr + 'T12:00:00');
  const dayOfWeek = date.getDay();
  const hours = businessHours[location] || businessHours.matriz;
  return hours[dayOfWeek];
};

// Día de la semana (0=Dom) y minutos del día en hora de Ecuador, extraídos con
// Intl.DateTimeFormat (robusto; antes new Date(toLocaleString()) fallaba en
// Safari/Firefox y devolvía "cerrado" siempre).
const getEcuadorDayAndMinutes = () => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Guayaquil',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).formatToParts(new Date());

  const get = (type) => parts.find((p) => p.type === type)?.value;
  const dayMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  const dayOfWeek = dayMap[get('weekday')];
  let hour = parseInt(get('hour'), 10);
  if (hour === 24) hour = 0; // algunos motores devuelven 24 a medianoche
  const minute = parseInt(get('minute'), 10);
  return { dayOfWeek, currentTime: hour * 60 + minute };
};

// Horario de HOY de un local, en hora de Ecuador: { open, close } o null si ese
// día está cerrado. Útil para textos cortos ("08:00–18:00") sin volcar la
// semana entera como hace getScheduleSummaryEs().
export const getTodayHours = (location) => {
  const table = businessHours[location];
  if (!table) return null;
  const { dayOfWeek } = getEcuadorDayAndMinutes();
  return table[dayOfWeek] || null;
};

// ¿Está abierto AHORA un local concreto? ('matriz' | 'sucursal' | 'produccion')
export const isLocationOpen = (location) => {
  const table = businessHours[location];
  if (!table) return false;
  const { dayOfWeek, currentTime } = getEcuadorDayAndMinutes();
  const hours = table[dayOfWeek];
  if (!hours) return false; // ese día el local está cerrado
  return currentTime >= timeToMinutes(hours.open) && currentTime <= timeToMinutes(hours.close);
};

// Estado de apertura por local + agregados. Cada local tiene SU horario:
// los domingos solo abre la Sucursal (09:00-13:00) y los lunes solo la Matriz.
// `delivery` es la ventana de entrega (unión de los locales que despachan):
// puede estar despachando a domicilio desde las 08:00 con las dos tiendas
// todavía cerradas, así que `anyOpen` lo incluye — si no, el aviso "estamos
// cerrados" saldría a las 08:30 con pedidos perfectamente tomables.
export const getOpenStatus = () => {
  const matriz = isLocationOpen('matriz');
  const sucursal = isLocationOpen('sucursal');
  const delivery = isDeliveryOpen();
  return { matriz, sucursal, delivery, anyOpen: matriz || sucursal || delivery };
};

// Compat: "tienda" = Matriz (la lógica de pedidos/entrega usa Matriz).
export const isStoreCurrentlyOpen = () => isLocationOpen('matriz');
